import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { statIncrease, statIncreasePercent } from './factories';
import { ParamReader } from './ParamReader';

export const stats: Record<number, IBlowDef> = {
    1: { ...statIncrease('PATK'), wikiDesc: 'Add Mod PATK.' },
    2: {
        name: 'Skill physical damage %',
        wikiDesc: 'Skill physical damage multiplier becomes 1+Mod%.',
        describe({ params }) {
            const modValue = params.nextNum();
            if (isNaN(modValue)) {
                return null;
            }
            return ['Skill physical damage multiplier becomes ', seg.pct(1 + modValue)];
        },
    },
    3: { ...statIncrease('PDEF'), wikiDesc: 'Add Mod PDEF.' },
    4: {
        name: 'PDEF %',
        wikiDesc: 'Physical damage taken multiplier becomes 1+Mod%.',
        describe({ params }) {
            const modValue = params.nextNum();
            if (isNaN(modValue)) {
                return null;
            }
            return ['Physical damage taken multiplier becomes ', seg.pct(1 + modValue)];
        },
    },
    5: statIncrease('STR'),
    6: { ...statIncrease('AGI'), wikiDesc: 'Add Mod AGI.' },
    7: { ...statIncrease('INT'), wikiDesc: 'Add Mod INT.' },
    8: { ...statIncrease('VIT'), wikiDesc: 'Add Mod VIT.' },
    9: { ...statIncrease('HP'), wikiDesc: 'Add Mod HP.' },
    10: {
        ...statIncrease('MP'),
        wikiDesc: 'Add Mod MP.',
    },
    19: { ...statIncrease('Para'), wikiDesc: 'Add Mod para.' },
    20: { ...statIncrease('Para Resist'), wikiDesc: 'Add Mod para resist.' },
    21: { ...statIncrease('Crit'), wikiDesc: 'Add Mod crit.' },
    22: { ...statIncrease('Crit Resist'), wikiDesc: 'Add Mod crit resist.' },
    23: { ...statIncrease('Stun'), wikiDesc: 'Add Mod stun.' },
    24: { ...statIncrease('Stun Resist'), wikiDesc: 'Add Mod stun resist.' },
    25: {
        name: 'Animation Speed %',
        wikiDesc: 'Animation speed multiplier becomes 1+Mod%.',
        describe({ params }) {
            const modValue = params.nextNum();
            if (isNaN(modValue)) {
                return null;
            }
            return ['Animation speed multiplier becomes ', seg.pct(1 + modValue)];
        },
    },
    28: { ...statIncrease('MDMG'), wikiDesc: 'Add Mod MATK.' },
    29: { ...statIncreasePercent('MDMG'), wikiDesc: 'Add Mod MATK%.' },
    30: {
        name: 'Block %',
        wikiDesc: 'Gives Chance% chance to block incoming attacks.',
        describe({ params }) {
            const chance = params.nextNum();
            if (isNaN(chance)) {
                return null;
            }
            return ['Gives a ', seg.pct(chance), ' chance to block incoming attacks'];
        },
    },
    31: {
        name: 'Parry %',
        wikiDesc: 'Gives Chance% chance to parry incoming attacks. DamageToTake% (0.0-1.0), if nonzero, ' +
            'lets that fraction of the incoming damage through the parry. MaxHP%CapPerHit (0.0-1.0) caps ' +
            'the damage taken per hit at that fraction of max HP.',
        describe({ params }) {
            const f = params.numbers();
            const chance = f[0];
            if (isNaN(chance)) {
                return null;
            }
            const out: Segment[] = ['Gives a ', seg.pct(chance), ' chance to parry incoming attacks'];
            const damageToTake = f[1];
            if (damageToTake) {
                out.push(', letting ', seg.pct(damageToTake), ' of the damage through');
            }
            const maxHpCap = f[2];
            if (maxHpCap) {
                out.push(', capped at ', seg.pct(maxHpCap), ' of max HP per hit');
            }
            return out;
        },
    },
    32: { ...statIncreasePercent('Fire'), wikiDesc: 'Add Mod% fire attack.' },
    33: { ...statIncreasePercent('Water'), wikiDesc: 'Add Mod% water attack.' },
    34: { ...statIncreasePercent('Light'), wikiDesc: 'Add Mod% light attack.' },
    35: { ...statIncreasePercent('Dark'), wikiDesc: 'Add Mod% dark attack.' },
    36: { ...statIncreasePercent('Fire Resist'), wikiDesc: 'Add Mod% fire resist.' },
    37: { ...statIncreasePercent('Water Resist'), wikiDesc: 'Add Mod% water resist.' },
    38: { ...statIncreasePercent('Light Resist'), wikiDesc: 'Add Mod% light resist.' },
    39: { ...statIncreasePercent('Dark Resist'), wikiDesc: 'Add Mod% dark resist.' },
    58: { ...statIncreasePercent('Max HP'), wikiDesc: 'Increase max HP by Mod%.' },
    59: { ...statIncreasePercent('Max MP'), wikiDesc: 'Increase max MP by Mod%.' },
    61: { ...statIncrease('Super Armor'), wikiDesc: 'Increase super armor by Mod.' },
    64: { ...statIncreasePercent('Super Armor'), wikiDesc: 'Increase super armor by Mod%.' },
    65: {
        ...statIncrease('Weapon Attack Range'),
        wikiDesc: 'Increases attack range by Mod meters (probably only works on bows).',
    },
    75: { ...statIncrease('Movement Speed'), wikiDesc: 'Increase movement speed by Mod.' },
    76: { ...statIncreasePercent('Movement Speed'), wikiDesc: 'Increase movement speed by Mod%.' },
    87: statIncreasePercent('STR'),
    88: { ...statIncreasePercent('AGI'), wikiDesc: 'Add Mod% AGI.' },
    89: { ...statIncreasePercent('INT'), wikiDesc: 'Add Mod% INT.' },
    90: { ...statIncreasePercent('VIT'), wikiDesc: 'Add Mod% VIT.' },
    93: { ...statIncrease('MDEF'), wikiDesc: 'Add Mod MDEF.' },
    94: { ...statIncreasePercent('MDEF'), wikiDesc: 'Add Mod% MDEF.' },
    109: {
        name: 'Add Victim PATK to hits taken',
        wikiDesc: "Debuff: incoming physical hits gain the victim's own PATK * Ratio as extra attack. " +
            'Multiple stacks sum.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return [
                'Incoming physical hits gain an extra ', seg.pct(ratio),
                " of the target's own PATK as attack (stacks add)",
            ];
        },
    },
    110: {
        name: 'Add Victim MATK to hits taken',
        wikiDesc: "Debuff: incoming magic hits gain the victim's own MATK * Ratio as extra attack. " +
            'Multiple stacks sum.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return [
                'Incoming magic hits gain an extra ', seg.pct(ratio),
                " of the target's own MATK as attack (stacks add)",
            ];
        },
    },
    111: {
        name: 'Reflect melee physical damage',
        wikiDesc: 'Reflects Ratio% of incoming melee physical damage to the attacker.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Reflects ', seg.pct(ratio), ' of incoming melee physical damage to the attacker'];
        },
    },
    112: {
        name: 'Reflect ranged physical damage',
        wikiDesc: 'Reflects Ratio% of incoming ranged physical damage to the attacker.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Reflects ', seg.pct(ratio), ' of incoming ranged physical damage to the attacker'];
        },
    },
    113: {
        name: 'Reflect melee magic damage',
        wikiDesc: 'Reflects Ratio% of incoming melee magical damage to the attacker.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Reflects ', seg.pct(ratio), ' of incoming melee magic damage to the attacker'];
        },
    },
    114: {
        name: 'Reflect ranged magic damage',
        wikiDesc: 'Reflects Ratio% of incoming ranged magical damage to the attacker.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Reflects ', seg.pct(ratio), ' of incoming ranged magic damage to the attacker'];
        },
    },
    123: { ...statIncreasePercent('Paralyze'), wikiDesc: 'Increase paralyze (stagger power dealt) by Mod%.' },
    124: { ...statIncreasePercent('Para Resist'), wikiDesc: 'Increase paralyze resist by Mod%.' },
    125: { ...statIncreasePercent('Critical'), wikiDesc: 'Increase critical chance stat by Mod%.' },
    126: { ...statIncreasePercent('Critical Resist'), wikiDesc: 'Increase critical resist by Mod%.' },
    127: { ...statIncreasePercent('Stun'), wikiDesc: 'Increase stun by Mod%.' },
    128: { ...statIncreasePercent('Stun Resist'), wikiDesc: 'Increase stun resist by Mod%.' },
    134: {
        name: 'Physical damage taken %',
        wikiDesc: 'Multiplies physical damage taken by Ratio (0.75 = takes 25% less, 1.2 = 20% more). ' +
            'Multiple instances stack additively on (Ratio - 1). A trailing /-1 also applies the modifier ' +
            'to magic damage.',
        describe({ raw }) {
            const slash = raw.indexOf('/');
            const ratio = ParamReader.num((slash >= 0 ? raw.slice(0, slash) : raw).trim());
            if (isNaN(ratio)) {
                return null;
            }
            const delta = ratio - 1;
            const dir = delta < 0 ? 'decreased' : 'increased';
            const out: Segment[] = [`Physical damage taken ${dir} by `, seg.pct(Math.abs(delta))];
            if (slash >= 0 && raw.slice(slash + 1).trim() === '-1') {
                out.push(' (also applies to magic damage)');
            }
            return out;
        },
    },
    135: {
        name: 'Magic damage taken %',
        wikiDesc: 'Multiplies magic damage taken by Ratio. Same stacking as 134 (additive on Ratio - 1). ' +
            'A trailing /-1 also applies the modifier to physical damage.',
        describe({ raw }) {
            const slash = raw.indexOf('/');
            const ratio = ParamReader.num((slash >= 0 ? raw.slice(0, slash) : raw).trim());
            if (isNaN(ratio)) {
                return null;
            }
            const delta = ratio - 1;
            const dir = delta < 0 ? 'decreased' : 'increased';
            const out: Segment[] = [`Magic damage taken ${dir} by `, seg.pct(Math.abs(delta))];
            if (slash >= 0 && raw.slice(slash + 1).trim() === '-1') {
                out.push(' (also applies to physical damage)');
            }
            return out;
        },
    },
    142: {
        name: 'SAB%',
        wikiDesc: 'Changes the super armor break value dealt by the target by rate%.',
        describe({ params }) {
            const rate = params.nextNum();
            if (isNaN(rate)) {
                return null;
            }
            const dir = rate < 0 ? 'decreased' : 'increased';
            return [`Super armor break dealt on hit ${dir} by `, seg.pct(Math.abs(rate))];
        },
    },
    144: {
        name: 'Movement/Action/Ice Resist',
        wikiDesc: 'Adjusts movement speed by Move%, action speed by Action%, and ice defense ratio by ' +
            'IceResist%.',
        note: 'The third field is an ice defense ratio, not a speed stat.',
        describe({ params }) {
            const f = params.numbers();
            const parts: Segment[][] = [];
            const push = (label: string, v: number) => {
                if (!v) {
                    return;
                }
                const dir = v < 0 ? 'reduced' : 'increased';
                parts.push([`${label} ${dir} by `, seg.pct(Math.abs(v))]);
            };
            push('movement speed', f[0]);
            push('action speed', f[1]);
            push('ice defense', f[2]);
            if (!parts.length) {
                return null;
            }
            const out: Segment[] = [];
            parts.forEach((p, i) => {
                if (i > 0) {
                    out.push('; ');
                }
                out.push(...p);
            });
            return out;
        },
    },
    153: {
        name: 'Parry with cooldown',
        wikiDesc: "Parries incoming attacks at Rate% chance, gated by the parent skill's cooldown.",
        describe({ params }) {
            const rate = params.nextNum();
            if (isNaN(rate)) {
                return null;
            }
            return [
                'Gives a ', seg.pct(rate),
                " chance to parry incoming attacks, gated by the parent skill's cooldown",
            ];
        },
    },
    164: {
        name: 'STR to ATK',
        wikiDesc: "Adds STR * Value as flat attack (snapshot at apply time, doesn't update if STR " +
            "changes later), capped at Limit * the caster's max MATK.",
        note: 'All 3 params are required; an omitted Limit is uninitialized memory, not "no cap".',
        describe({ params }) {
            const f = params.numbers();
            const value = f[0];
            const attackType = f[1];
            const limit = f[2];
            if (isNaN(value) || isNaN(attackType) || isNaN(limit)) {
                return null;
            }
            return [
                'Adds STR × ', seg.num(value), ' as flat ',
                seg.en(attackType, ['magic', 'physical'], 'unknown'),
                ' attack (snapshot on apply), capped at ', seg.pct(limit),
                " of the caster's max MATK",
            ];
        },
    },
    165: {
        name: 'Increase PDMG',
        wikiDesc: 'Increase in physical attack power by 1 per 1 intellect.',
        note: 'Parameter format ("1;stat;Increase") is undocumented.',
    },
    200: { ...statIncrease('PDMG'), wikiDesc: 'Add Mod PDMG.' },
    201: { ...statIncreasePercent('PDMG'), wikiDesc: 'Add Mod PDMG%.' },
    202: { ...statIncrease('MDMG'), wikiDesc: 'Add Mod MDMG.' },
    203: {
        ...statIncreasePercent('MDMG'),
        wikiDesc: 'Add Mod MDMG%.',
        note: 'Applied at equip level, before skill state effects, like other 200-series blows.',
    },
    209: {
        name: 'BasisAttackDmgUp',
        wikiDesc: 'Increases basic (non-skill) attack damage by Ratio. Multiple instances do not ' +
            'stack (the first applied instance wins); disabled while an EX stance (121/129) is active.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return [
                'Increases basic attack damage by ', seg.pct(ratio),
                ' (first instance wins, does not stack; disabled while an EX stance (', seg.blow(121),
                '/', seg.blow(129), ') is active)',
            ];
        },
    },
    213: {
        name: 'Attack Power increase %',
        wikiDesc: "Adds your PATK * Ratio to every hit's attack.",
        note: 'Bugged: checks the victim instead of the hitter, so it only fires if the victim has this effect too.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Adds your PATK × ', seg.pct(ratio), " to every hit's attack"];
        },
    },
    214: {
        name: 'Magic Power increase %',
        wikiDesc: "Adds your MATK * Ratio to every hit's attack, regardless of the hit's type. " +
            'Instances sum.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return [
                'Adds your MATK × ', seg.pct(ratio),
                " to every hit's attack, regardless of hit type (instances sum)",
            ];
        },
    },
    220: {
        name: 'Accel',
        wikiDesc: 'Delayed state: action and movement speed are reduced. Values are already ' +
            'percentages, not ratios (e.g. -60 means -60%, not -0.6).',
        describe({ params }) {
            const f = params.numbers();
            const actionSpeed = f[0];
            const movementSpeed = f[1];
            if (isNaN(actionSpeed) || isNaN(movementSpeed)) {
                return null;
            }
            const parts: Segment[][] = [];
            const push = (label: string, v: number) => {
                if (!v) {
                    return;
                }
                const dir = v < 0 ? 'reduced' : 'increased';
                parts.push([`${label} ${dir} by `, seg.pct(Math.abs(v) / 100)]);
            };
            push('action speed', actionSpeed);
            push('movement speed', movementSpeed);
            if (!parts.length) {
                return null;
            }
            const out: Segment[] = [];
            parts.forEach((p, i) => {
                if (i > 0) {
                    out.push('; ');
                }
                out.push(...p);
            });
            return out;
        },
    },
    221: {
        name: 'Prefix Damage',
        wikiDesc: "Adds attack matching the hit's type: physical hits gain PATK * Ratio, magical hits " +
            'gain MATK * Ratio. Instances sum.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return [
                'Adds ', seg.pct(ratio),
                " of the hit's matching attack stat (PATK for physical hits, MATK for magical hits) " +
                'as attack (instances sum)',
            ];
        },
    },
    222: {
        name: 'Critical probability',
        wikiDesc: 'Increases the critical chance when attacking enemies afflicted with burn (42).',
        describe({ params }) {
            const f = params.numbers();
            const chance = f[0];
            if (isNaN(chance)) {
                return null;
            }
            const out: Segment[] = [
                'Against targets afflicted with ', seg.blow(42), ', increases critical chance by ',
                seg.pct(chance),
            ];
            const flat = f[1];
            if (flat) {
                out.push(' plus ', seg.num(flat), ' flat critical');
            }
            return out;
        },
    },
    234: { ...statIncrease('Crit'), wikiDesc: 'Add Mod crit.' },
    250: {
        name: 'Add Target Critical Probability',
        wikiDesc: 'Increase Critical Probability to Target by Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return [
                'Attacks landing against the holder gain ', seg.pct(mod), ' additional critical chance',
            ];
        },
    },
    251: {
        ...statIncreasePercent('Critical'),
        wikiDesc: 'Add Mod% crit. Takes your critical chance % and adds the flat Mod% to it. For ' +
            'example, 80% crit chance + 0.1 (Mod%) is 90% crit chance.',
    },
    306: {
        ...statIncreasePercent('All Elemental Resist'),
        wikiDesc: 'Add Mod% all elemental resist.',
    },
    307: {
        ...statIncreasePercent('All Elemental ATK'),
        name: 'All Elemental Increase',
        wikiDesc: 'Add Mod% to all elements.',
    },
    310: {
        ...statIncreasePercent('ATK'),
        wikiDesc: 'Add Mod% PATK and MATK.',
    },
    313: {
        ...statIncrease('FD'),
        name: 'Flat Final Damage (FD)',
        wikiDesc: 'Add a flat amount of FD stat (not % based).',
    },
    334: {
        name: 'Critical Fixed',
        wikiDesc: "Forces the actor's critical stat to an absolute value at the very end of stat calc, " +
            'overriding all other modifiers.',
        note: 'The optional second field (attackValue) has no effect.',
        describe({ params }) {
            const critValue = params.nextNum();
            if (isNaN(critValue)) {
                return null;
            }
            return ["Forces the actor's critical stat to ", seg.num(critValue)];
        },
    },
    336: {
        name: 'Additional % Max Damage per Hit',
        wikiDesc: 'Deals Mod/100% of Max Damage per hit.',
        note: 'Trailing params (0;1) are undocumented; only Mod is used.',
        describe({ params }) {
            const f = params.numbers();
            if (f.length < 3 || isNaN(f[2])) {
                return null;
            }
            return ['Deals ', seg.pct(f[2] / 100), ' of Max Damage per hit'];
        },
    },
    346: {
        name: 'Equalize STR/AGI/INT',
        wikiDesc: 'Takes the highest of STR/AGI/INT and sets the others to be the same.',
        note: 'No parameters — pass 0.',
        describe() {
            return ['Sets STR/AGI/INT to all equal the highest of the three'];
        },
    },
    354: {
        name: 'Item enhancement chance modifier',
        wikiDesc: 'Modifies the enhancement rate of items (at Belin): success chance += chance * ' +
            'Value/100.',
        describe({ params }) {
            const value = params.nextNum();
            if (isNaN(value)) {
                return null;
            }
            return [
                'Increases item enhancement success chance by ', seg.pct(value / 100),
                ' of its current value',
            ];
        },
    },
    389: {
        ...statIncreasePercent('Final Damage'),
        wikiDesc: 'Add Mod% FD. Also works on individual skills via a trailing list of skill IDs.',
    },
    404: {
        ...statIncreasePercent('Crit Damage'),
        wikiDesc: 'Add Mod% crit damage.',
    },
    421: {
        ...statIncrease('Crit Damage'),
        wikiDesc: 'Adds a flat, absolute amount of critical damage.',
    },
    422: {
        name: 'Add Season Status',
        wikiDesc: 'Adds Amount to a season stat while active; instances sum. Only StatType 109 (Cold ' +
            'Resistance, Frozen World adaptation) is implemented — other StatType IDs are ignored.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const items: Segment[][] = [];
            for (let i = 0; i + 1 < f.length; i += 2) {
                const statType = ParamReader.num(f[i]);
                const amount = ParamReader.num(f[i + 1]);
                if (isNaN(statType) || isNaN(amount)) {
                    continue;
                }
                const item: Segment[] = [
                    statType === 109 ? seg.val('Cold Resistance') : seg.val(`StatType ${statType}`),
                    ': ', seg.num(amount),
                ];
                if (statType !== 109) {
                    item.push(' (not implemented, ignored)');
                }
                items.push(item);
            }
            if (!items.length) {
                return null;
            }
            return ['Adds season stats (instances sum): ', seg.list(items)];
        },
    },
};
